import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Info, Loader2, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { QuoteScoreMissingItem, QuoteScoreResponse } from '@/api/types';

interface QuoteScoreSectionProps {
  readonly data: QuoteScoreResponse;
  readonly onRescore: () => void;
  readonly isRescoring: boolean;
  readonly onAddItem: (item: QuoteScoreMissingItem) => void;
  readonly onAddAllItems: (items: QuoteScoreMissingItem[]) => void;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  if (score >= 50) return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
}

export function QuoteScoreSection({
  data,
  onRescore,
  isRescoring,
  onAddItem,
  onAddAllItems,
}: Readonly<QuoteScoreSectionProps>) {
  const { t } = useTranslation();
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());

  const handleAddItem = (item: QuoteScoreMissingItem) => {
    setAddedItems((prev) => new Set(prev).add(item.description));
    onAddItem(item);
  };

  const handleAddAll = () => {
    const unadded = data.missing_items.filter((item) => !addedItems.has(item.description));
    const allDescriptions = new Set(data.missing_items.map((item) => item.description));
    setAddedItems(allDescriptions);
    onAddAllItems(unadded);
  };

  const allAdded =
    data.missing_items.length > 0 &&
    data.missing_items.every((item) => addedItems.has(item.description));

  return (
    <div data-testid="quote-score-section" className="mt-4 space-y-3">
      {/* Header with title, score badge, and re-score button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          <h3 className="text-base font-semibold">{t('quoteScore.title')}</h3>
          <Badge data-testid="score-badge" className={getScoreColor(data.overall_score)}>
            {data.overall_score}/100
          </Badge>
        </div>
        <Button
          data-testid="rescore-btn"
          variant="ghost"
          size="sm"
          onClick={onRescore}
          disabled={isRescoring}
          className="min-h-[44px]"
        >
          {isRescoring ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-1">{t('quoteScore.rescore')}</span>
        </Button>
      </div>

      {/* Missing items section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm">
            <span>{t('quoteScore.missingItems')}</span>
            {data.missing_items.length > 1 && (
              <Button
                data-testid="add-all-btn"
                variant="outline"
                size="sm"
                onClick={handleAddAll}
                disabled={allAdded}
                className="min-h-[44px]"
              >
                <Plus className="h-4 w-4" />
                <span className="ml-1">{t('quoteScore.addAll')}</span>
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.missing_items.length === 0 ? (
            <p data-testid="missing-items-empty" className="text-muted-foreground text-sm">
              {t('quoteScore.missingItemsEmpty')}
            </p>
          ) : (
            data.missing_items.map((item) => {
              const isAdded = addedItems.has(item.description);
              return (
                <div
                  key={item.description}
                  data-testid="missing-item"
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <div className="flex-1 space-y-0.5">
                    <div className="font-medium">{item.description}</div>
                    <div className="text-muted-foreground text-xs">
                      {item.estimated_quantity} x {item.estimated_unit_price}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t('quoteScore.reason')}: {item.reason}
                    </div>
                  </div>
                  {isAdded ? (
                    <Button
                      data-testid="item-added-btn"
                      variant="secondary"
                      size="sm"
                      disabled
                      className="min-h-[44px]"
                    >
                      <Check className="h-4 w-4" />
                      <span className="ml-1">{t('quoteScore.added')}</span>
                    </Button>
                  ) : (
                    <Button
                      data-testid="add-item-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddItem(item)}
                      className="min-h-[44px]"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="ml-1">{t('quoteScore.addItem')}</span>
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Pricing notes section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('quoteScore.pricingNotes')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.pricing_notes.length === 0 ? (
            <p data-testid="pricing-notes-empty" className="text-muted-foreground text-sm">
              {t('quoteScore.pricingNotesEmpty')}
            </p>
          ) : (
            data.pricing_notes.map((note) => (
              <div
                key={note.item_description}
                data-testid="pricing-note"
                className="flex items-start gap-2 text-sm"
              >
                {note.severity === 'warning' ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                ) : (
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                )}
                <div>
                  <span className="font-medium">{note.item_description}: </span>
                  <span className="text-muted-foreground">{note.note}</span>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Tips section */}
      {data.tips.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('quoteScore.tips')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {data.tips.map((tip) => (
                <li key={tip} data-testid="score-tip">
                  {tip}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
